import { Box, Chip, IconButton, Menu, MenuItem, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderIcon from "@mui/icons-material/Folder";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import React, { useState } from "react";

import type { ProjectFolder } from "../../api/projectFolders";
import { DEFAULT_FOLDER_BOUNDARY_STYLE, resolveBoundaryStyle, tintOverWhite } from "../../types/boundaryStyle";
import InlineNameField, { type InlineNameEdit } from "./InlineNameField";
import type { SubtreeCounts } from "./folderTree";
import { FROZEN_COLUMN_COUNT, FROZEN_RIGHT, ROW_BG, frozenCellSx } from "./frozenColumns";

interface FolderRowProps {
  folder: ProjectFolder;
  counts: SubtreeCounts;
  /** Total columns in the table; the folder's text spans all but the frozen ones. */
  colSpan: number;
  viewOnly: boolean;
  isDropTarget: boolean;
  /** The pointer is over this folder's box on the map. */
  highlighted: boolean;
  /** Pinned to the top of this folder list, for everyone. */
  isPinned: boolean;
  onTogglePin: () => void;
  /** Fly the map to the folder's extent; false when nothing inside has a location. */
  canLocate: boolean;
  onLocate: () => void;
  /** Drawn on the map (its own flag). */
  mapVisible: boolean;
  /** Name of a folder above it that is hidden from the map, if any. */
  hiddenBy: string | null;
  onToggleMapVisible: () => void;
  onOpen: () => void;
  onHoverChange: (hovering: boolean) => void;
  onRename: () => void;
  /** Set while the folder's name is being edited in place. */
  rename?: InlineNameEdit | null;
  onMove: () => void;
  onDelete: () => void;
  /** Open the boundary-layer dialog for this folder. */
  onBoundary: () => void;
  /** It has a boundary layer (drawn on the map along with its dashed box). */
  hasBoundary: boolean;
  dragProps: React.HTMLAttributes<HTMLElement> & { draggable?: boolean };
  dropProps: React.HTMLAttributes<HTMLElement>;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** A sub-folder of the open folder. Click opens it; resting shows its area on the map. */
export default function FolderRow({
  folder,
  counts,
  colSpan,
  viewOnly,
  isDropTarget,
  highlighted,
  isPinned,
  onTogglePin,
  canLocate,
  onLocate,
  mapVisible,
  hiddenBy,
  onToggleMapVisible,
  onOpen,
  onHoverChange,
  onRename,
  rename,
  onMove,
  onDelete,
  onBoundary,
  hasBoundary,
  dragProps,
  dropProps,
}: FolderRowProps) {
  const renaming = !!rename;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // Browsing story maps leads with how many there are inside.
  const summary = [
    counts.stories !== undefined ? plural(counts.stories, "story map") : null,
    plural(counts.projects, "project"),
    counts.folders ? plural(counts.folders, "folder") : null,
    hasBoundary ? "boundary" : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const drawn = mapVisible && !hiddenBy;
  const marked = highlighted || renaming;
  // The folder's colour on the map (its boundary style, amber by default)
  // colours its icon, highlight edge and highlight tint here, so a restyled
  // folder reads the same in both panes. Tints are blended opaque: the frozen
  // cells inherit them to cover scrolled content.
  const colour = resolveBoundaryStyle(folder.boundary_style, DEFAULT_FOLDER_BOUNDARY_STYLE).color;
  const highlightBg = tintOverWhite(colour, 0.14);
  const highlightHoverBg = tintOverWhite(colour, 0.24);
  const base = isDropTarget ? ROW_BG.dropTarget : marked ? highlightBg : ROW_BG.base;
  const hover = isDropTarget ? ROW_BG.dropTarget : marked ? highlightHoverBg : ROW_BG.hover;

  return (
    <TableRow
      // While the name is being edited the row neither opens nor drags, so
      // clicking and dragging inside the field just edit the text.
      onClick={renaming ? undefined : onOpen}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      {...(renaming ? {} : dragProps)}
      {...dropProps}
      sx={{
        cursor: renaming ? "default" : "pointer",
        borderLeft: marked ? `3px solid ${colour}` : "3px solid transparent",
        // Opaque colours: the frozen cells inherit them to cover scrolled content.
        backgroundColor: base,
        "&:hover": { backgroundColor: hover },
        transition: "background-color 120ms ease",
        ...(isDropTarget && { outline: "2px solid #00d4ad", outlineOffset: -2 }),
      }}
    >
      <TableCell colSpan={Math.max(1, colSpan - FROZEN_COLUMN_COUNT)} sx={{ py: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FolderIcon sx={{ color: colour }} />
          {rename ? (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <InlineNameField initialName={folder.name} ariaLabel="Folder name" messages="caption" {...rename} />
            </Box>
          ) : (
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600 }}>
                  {folder.name}
                </Typography>
                {/* Same amber chip as a project row, so an incident reads the
                    same whether it is in view or filed away in here. */}
                {counts.openIncidents > 0 && (
                  <Tooltip title="Open incidents in projects inside this folder">
                    <Chip
                      size="small"
                      icon={<LocalFireDepartmentOutlinedIcon sx={{ fill: "#fff" }} />}
                      label={`${counts.openIncidents} open incident${counts.openIncidents === 1 ? "" : "s"}`}
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: "#f59e0b",
                        color: "#fff",
                        fontWeight: 600,
                        "& .MuiChip-icon": { color: "#fff" },
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {summary}
              </Typography>
            </Box>
          )}
        </Box>
      </TableCell>

      {/* Frozen controls, in the same columns as a project row's. */}
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.pin)}>
        {(!viewOnly || isPinned) && (
          <Tooltip
            title={viewOnly ? "Pinned to the top for everyone" : isPinned ? "Unpin from top" : "Pin to top for everyone"}
            placement="left"
          >
            <span>
              <IconButton
                size="small"
                disabled={viewOnly}
                aria-label={isPinned ? "Unpin folder" : "Pin folder"}
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePin();
                }}
                sx={{
                  color: isPinned ? "#00d4ad" : "#9ca3af",
                  "&.Mui-disabled": { color: isPinned ? "#00d4ad" : "#d1d5db" },
                }}
              >
                {isPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.eye)}>
        <Tooltip
          placement="left"
          title={
            hiddenBy
              ? `Hidden from the map because “${hiddenBy}” is hidden`
              : viewOnly
                ? mapVisible
                  ? "Shown on the map"
                  : "Hidden from the map"
                : mapVisible
                  ? "Hide this folder and everything in it from the map (for everyone)"
                  : "Show on the map (for everyone)"
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={viewOnly || !!hiddenBy}
              aria-label={mapVisible ? "Hide from the map" : "Show on the map"}
              onClick={(e) => {
                e.stopPropagation();
                onToggleMapVisible();
              }}
              sx={{
                color: drawn ? "#64748b" : "#cbd5e1",
                "&.Mui-disabled": { color: drawn ? "#64748b" : "#cbd5e1" },
              }}
            >
              {drawn ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell padding="none" sx={frozenCellSx(FROZEN_RIGHT.locate)}>
        <Tooltip title={canLocate ? "Show on map" : "Nothing in this folder has a map area yet"} placement="left">
          <span>
            <IconButton
              size="small"
              disabled={!canLocate}
              aria-label="Show on map"
              onClick={(e) => {
                e.stopPropagation();
                onLocate();
              }}
              sx={{ color: "#64748b", "&:hover": { color: "#00d4ad" } }}
            >
              <MyLocationIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell padding="none" align="right" sx={frozenCellSx(FROZEN_RIGHT.menu, true)}>
        {!viewOnly && (
          <IconButton
            size="small"
            aria-label="Folder actions"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onRename();
            }}
          >
            <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Rename
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onMove();
            }}
          >
            <DriveFileMoveOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Move to folder…
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onBoundary();
            }}
          >
            <PentagonOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> {hasBoundary ? "Boundary…" : "Add boundary…"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDelete();
            }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}
